import React, { useEffect, useState } from 'react';
import { Calendar, MapPin, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { liveProgramsAPI, offlineCentersAPI } from '../utils/api';

const money = (price) => {
  const amount = price?.sale_amount_minor ?? price?.list_amount_minor ?? 0;
  return amount === 0 ? 'Free' : new Intl.NumberFormat('en-IN', { style: 'currency', currency: price?.currency || 'INR', maximumFractionDigits: 0 }).format(amount / 100);
};

const OfflinePage = () => {
  const [programs, setPrograms] = useState([]);
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([liveProgramsAPI.getAll({ delivery: 'offline' }), offlineCentersAPI.getAll({ per_page: 4 })])
      .then(([programResponse, centerResponse]) => {
        setPrograms(programResponse.data.data);
        setCenters(centerResponse.data.data);
      })
      .catch(() => setError('Offline programs are temporarily unavailable. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <main className="min-h-screen bg-black px-4 pb-16 pt-24 text-white">
      <div className="mx-auto max-w-7xl">
        <header className="mb-12 text-center">
          <h1 className="mb-4 text-4xl font-bold sm:text-5xl">Offline <span className="text-yellow-400">Programs</span></h1>
          <p className="mx-auto max-w-2xl text-lg text-gray-400">Classroom workshops and guided programs taught at our active centers.</p>
        </header>

        {loading && <p role="status" className="py-16 text-center text-gray-300">Loading offline programs…</p>}
        {error && <div role="alert" className="rounded-lg border border-red-700 bg-red-950 p-4 text-red-100">{error}</div>}

        {!loading && !error && (
          <>
            <section aria-labelledby="programs-heading" className="mb-16">
              <h2 id="programs-heading" className="mb-6 text-2xl font-bold">Upcoming classroom programs</h2>
              {programs.length === 0 ? <p className="rounded-xl border border-zinc-800 bg-zinc-900 p-8 text-gray-300">No classroom programs are scheduled right now.</p> : (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {programs.map((program) => (
                    <article key={program.id} className="flex flex-col rounded-xl border border-zinc-800 bg-zinc-900 p-6">
                      <p className="mb-2 text-sm uppercase tracking-wide text-yellow-400">{program.type}</p>
                      <h3 className="mb-3 text-xl font-bold">{program.title}</h3>
                      <p className="mb-5 flex-1 text-gray-400">{program.short_description}</p>
                      <div className="mb-5 flex items-center justify-between text-sm text-gray-300"><span>{program.mode}</span><strong className="text-white">{money(program.price)}</strong></div>
                      <Link className="rounded-lg bg-yellow-500 px-4 py-3 text-center font-semibold text-black hover:bg-yellow-400" to={`/live/${program.type}/${program.slug}`}>View program</Link>
                    </article>
                  ))}
                </div>
              )}
            </section>

            <section aria-labelledby="centers-heading">
              <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
                <h2 id="centers-heading" className="text-2xl font-bold">Training centers</h2>
                <Link className="font-semibold text-yellow-400 hover:text-yellow-300" to="/offline/centers">View all centers</Link>
              </div>
              {centers.length === 0 ? <p className="text-gray-400">No centers are active yet.</p> : (
                <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
                  {centers.map((center) => <Link key={center.id} to={`/offline/centers/${center.slug}`} className="rounded-xl border border-zinc-800 bg-zinc-900 p-5 hover:border-yellow-500">
                    <MapPin aria-hidden="true" className="mb-3 text-yellow-400" />
                    <h3 className="font-bold">{center.name}</h3>
                    <p className="mt-1 text-sm text-gray-400">{center.city}, {center.state}</p>
                    <p className="mt-3 flex items-center gap-2 text-sm text-gray-300"><Calendar size={16} />{center.upcoming_sessions_count} upcoming</p>
                  </Link>)}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </main>
  );
};

export default OfflinePage;
